import { markIntentionalFlowPause } from "@/utils/sentry/intentional";
import { requestSMSConfirmation, requestSMSSending } from "@/api/sms";
import { sendStepUserJourney } from "@/api/userJourney";
import useInterval from "@/hooks/useInterval";
import usePageSubmit from "@/hooks/usePageSubmit";
import useTabsValidation from "@/hooks/useTabsValidation";
import { useTagManager } from "@/hooks/useTagManager";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { useCallback, useEffect, useRef, useState } from "react";

const pageConfig = {
  route: "/short-flow-sms-confirmation",
  prevRoute: "/type-product",
  nextRoute: "/confirmation/auto-debt",
};

export function useShortFlowSMSConfirmation() {
  useTabsValidation(pageConfig.route, pageConfig.prevRoute);
  const [error, setError] = useState("");
  const [, setSmsCode] = useState("");
  const [clearVerificationCode, setClearVerificationCode] = useState(false);

  const [timer, setTimer] = useInterval();

  const phoneNumber = useFormStore((store) => store.value.tel);
  const saleId = useFormStore((store) => store.saleId);


  const [sendSMSIsLoading, setSendSMSIsLoading] = useState(false);
  const [smsSentConfirmation, setSmsSentConfirmation] = useState(false);

  const sent = useRef(false);

  const handleTokenResend = async () => {
    setSmsCode("");
    setClearVerificationCode(true);
    try {
      setSendSMSIsLoading(true);
      await requestSMSSending({ saleID: saleId!, msisdn: phoneNumber }).finally(
        () => setSendSMSIsLoading(false)
      );
      setClearVerificationCode(false);
      setTimer(60);
    } catch (_err) {
      // Erro ja capturado pelo interceptor global do axios.
      setError(
        `Aconteceu um erro ao reenviar seu token. Por favor, tente novamente.`
      );
    }
  };
  const uuid = useFormStore((store) => store.value.uuid);

  const [SMSConfirmationLoading, setSMSConfirmationLoading] = useState(false);
  const { dataLayer } = useTagManager();
  const productStore = useProductStore((store) => store.item);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const tel = useFormStore((store) => store.value.tel);
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(tel).slice(0, 2))
  );
  const handleSMSReceiver = useCallback(
    async (data: { sms?: string } = {}) => {
      const sms = data.sms ?? "";
      try {
        setSMSConfirmationLoading(true);
        await requestSMSConfirmation({
          saleID: saleId!,
          msisdn: phoneNumber,
          sms,
        });
        dataLayer({
          event: "step",
          passo: "SMS",
          sku: removeAccentsAndSetUpperCase(productStore?.tim_id),
          nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
            productStore?.nome
          )}-${totalGbProduct}`,
          valor: formatPrice(currentPrice),
          status: "Sucesso",
        });
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errors?: { message?: string }[]; message?: string } } };
        const apiError =
          axiosErr?.response?.data?.errors?.[0]?.message ||
          axiosErr?.response?.data?.message;

        setError(
          apiError ||
            "Por favor, digite o código de segurança válido e continue"
        );
        dataLayer({
          event: "step",
          passo: "SMS",
          sku: removeAccentsAndSetUpperCase(productStore?.tim_id),
          nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
            productStore?.nome
          )}-${totalGbProduct}`,
          valor: formatPrice(currentPrice),
          status: "Erro",
        });
        // Erro real ja foi capturado pelo interceptor do axios; throw aqui eh
        // apenas para "pausar" o fluxo apos exibir mensagem ao usuario.
        throw markIntentionalFlowPause(err);
      } finally {
        setSMSConfirmationLoading(false);
      }
    },
    [
      saleId,
      phoneNumber,
      dataLayer,
      productStore?.tim_id,
      productStore?.nome,
      totalGbProduct,
      currentPrice,
    ]
  );

  const handleOnSubmit = usePageSubmit(pageConfig.nextRoute, handleSMSReceiver);

  const handleOnComplete = (sms: string) => {
    setSmsCode(sms);
    handleOnSubmit({ sms });
  };

  const handleOnChange = () => setError("");

  useEffect(() => {
    sendStepUserJourney({
      passo: "TOKEN",
      uuid,
    });
  }, [uuid]);

  useEffect(() => {
    if (saleId && !sent.current) {
      sent.current = true;
      requestSMSSending({
        saleID: saleId!,
        msisdn: phoneNumber,
      })
        .then(() => {
          setSmsSentConfirmation(true);
        })
        .catch((_err) => {
          // Erro ja capturado pelo interceptor global do axios.
          setError("Não foi possível enviar o SMS. Tente reenviar o código.");
        });
    }
  }, [phoneNumber, saleId]);

  useEffect(() => {
    if (smsSentConfirmation) {
      const timeout = setTimeout(() => setSmsSentConfirmation(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [smsSentConfirmation]);

  const maskedPhone = phoneNumber
    ? (() => {
        const digits = getOnlyNumbers(phoneNumber);
        const ddd = digits.slice(0, 2);
        const last4 = digits.slice(-4);
        return `(${ddd}) ${"*".repeat(Math.max(0, digits.length - 6))}-${last4}`;
      })()
    : "";

  return {
    phoneNumber,
    clearVerificationCode,
    SMSConfirmationLoading,
    error,
    timer,
    sendSMSIsLoading,
    smsSentConfirmation,
    maskedPhone,
    pageConfig,
    handleOnSubmit,
    setSmsCode,
    handleOnChange,
    handleOnComplete,
    handleTokenResend,
  };
}
